import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Popover,
  Badge,
} from '@mui/material'
import {
  Add,
  Send,
  PlayArrow,
  Pause,
  PlayCircleOutline,
  PauseCircle,
  MenuBook,
  Map as MapIcon,
  NoteAlt,
  AccessTime,
  Lightbulb,
  Forum as ChatIcon,
} from '@mui/icons-material'
import { keyframes } from '@mui/material'
import { SessionInputType } from '@api/types/session'
import { getChallengeIcon } from '../ChallengeActionButton'
import { Marker, Popup, Circle, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import { LazyMap } from '@components/map/LazyMap'
import { useGeolocation } from '@hooks/useGeolocation'
import { useSessionJournal } from '../../../hooks/useSessionJournal'
import { JournalEntryDto, JournalEntryType } from '@api/types/session'
import { AdventureDto } from '@api/types/adventure'
import { RevealedPoiLocation, useSessionStore } from '../../../stores/sessionStore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '@api/endpoints/chat'
import { useChatStore } from '@store/chatStore'
import { useAuthStore } from '@store/authStore'
import { useGeneralChatConnection } from '@hooks/useSignalR'
import { MessageThread } from '@features/chat/components/MessageThread'
import { MessageInput } from '@features/chat/components/MessageInput'

const activeGlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`

interface JournalNotebookProps {
  sessionId: string | null
  adventure: AdventureDto
  locations: RevealedPoiLocation[]
  inputArea?: React.ReactNode
  // Clue props
  hasAvailableClues?: boolean
  onRequestClue?: () => void
  // Challenge props
  hasPendingChallenge?: boolean
  challengeInputType?: SessionInputType
  onChallengeClick?: () => void
  // Group chat props
  conversationId?: string | null
  isGroup?: boolean
  chatOpenTrigger?: number
}

// Entry types that should be visible to users
// Note: PoiRevealed and RouteRevealed are excluded since users can see them on the map
const VISIBLE_ENTRY_TYPES = [
  JournalEntryType.FormattedMessage,
  JournalEntryType.TextClue,
  JournalEntryType.ClueRequested,
  JournalEntryType.UserNote,
  JournalEntryType.ChoiceMade,
  JournalEntryType.AnswerSubmitted,
  JournalEntryType.CodexInputEntered,
  JournalEntryType.NumericInputEntered,
  JournalEntryType.Image,
  JournalEntryType.AudioPlayed,
  JournalEntryType.Video,
  JournalEntryType.SessionCompleted,
  JournalEntryType.SessionFailed,
  JournalEntryType.ModeratorApprovalReceived,
]

// Page types for navigation
type PageType = 'description' | 'map' | 'journal' | 'chat'

// Custom icon for user location on map
const userLocationIcon = new L.DivIcon({
  className: 'user-location-marker',
  html: `
    <div style="
      width: 20px;
      height: 20px;
      background: #2196F3;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "></div>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10],
})

// Create custom icon for POI markers
function createPoiIcon(emoji: string): L.DivIcon {
  return new L.DivIcon({
    className: 'poi-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: white;
        border: 2px solid #333;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">${emoji}</div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  })
}

// Helper component to focus map on a location
function MapFocuser({ location }: { location: { lat: number; lng: number } | null }) {
  const map = useMap()

  useEffect(() => {
    if (location && map) {
      // Small delay to ensure map is fully initialized after lazy load
      const timer = setTimeout(() => {
        try {
          map.setView([location.lat, location.lng], 16, { animate: true })
        } catch (e) {
          console.warn('Failed to set map view:', e)
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [map, location])

  return null
}

// Interface for revealed POI from journal entry
interface RevealedPoiFromJournal {
  id: string
  nodeId: string
  title: string
  latitude: number
  longitude: number
  markerEmoji?: string
  markerStyle?: string
  circleRadius?: number
  circleColor?: string
}

// Interface for revealed route from journal entry
interface RevealedRouteFromJournal {
  id: string
  nodeId: string
  title: string
  routePoints: [number, number][]
  lineColor: string
  lineStyle: string
  lineOpacity: number
  arrowStyle: string
  distanceMeters?: number
  durationSeconds?: number
}

// Helper to get dash array for line styles
function getLineDashArray(lineStyle: string): string | undefined {
  switch (lineStyle?.toLowerCase()) {
    case 'dashed':
      return '10, 6'
    case 'dotted':
      return '2, 6'
    default:
      return undefined
  }
}

// Calculate bearing between two points for arrow direction
function calculateBearing(start: [number, number], end: [number, number]): number {
  const startLat = start[0] * Math.PI / 180
  const startLng = start[1] * Math.PI / 180
  const endLat = end[0] * Math.PI / 180
  const endLng = end[1] * Math.PI / 180

  const dLng = endLng - startLng
  const x = Math.sin(dLng) * Math.cos(endLat)
  const y = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng)

  const bearing = Math.atan2(x, y) * 180 / Math.PI
  return (bearing + 360) % 360
}

// Create arrow icon for route direction indicators
function createRouteArrowIcon(color: string, rotation: number): L.DivIcon {
  return new L.DivIcon({
    className: 'route-arrow-marker',
    html: `
      <div style="
        transform: rotate(${rotation}deg);
        color: ${color};
        font-size: 16px;
        line-height: 1;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
      ">▶</div>
    `,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  })
}

// Animated polyline component for flowing route effect
function AnimatedRoutePolyline({
  positions,
  color,
  opacity,
  weight = 4,
}: {
  positions: [number, number][]
  color: string
  opacity: number
  weight?: number
}) {
  const polylineRef = useRef<L.Polyline | null>(null)

  useEffect(() => {
    if (polylineRef.current) {
      const element = polylineRef.current.getElement() as SVGElement | null
      if (element) {
        // Add CSS animation for flowing effect
        element.style.strokeDasharray = '12, 8'
        element.style.animation = 'routeFlowAnimation 1s linear infinite'
      }
    }
  }, [])

  // Inject CSS keyframes if not already present
  useEffect(() => {
    const styleId = 'route-flow-animation-player'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        @keyframes routeFlowAnimation {
          from { stroke-dashoffset: 20; }
          to { stroke-dashoffset: 0; }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  return (
    <Polyline
      ref={polylineRef}
      positions={positions}
      pathOptions={{
        color,
        weight,
        opacity,
      }}
    />
  )
}

export default function JournalNotebook({ sessionId, adventure, locations, inputArea, hasAvailableClues, onRequestClue, hasPendingChallenge, challengeInputType, onChallengeClick, conversationId, isGroup, chatOpenTrigger }: JournalNotebookProps) {
  const startedAt = useSessionStore((s) => s.startedAt)
  const participants = useSessionStore((s) => s.participants)
  const [currentPage, setCurrentPage] = useState(0) // 0 = description, 1 = map, 2 = journal, 3 = chat
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [playingAudio, setPlayingAudio] = useState<string | null>(null)
  const [videoDialog, setVideoDialog] = useState<{ open: boolean; url: string; title: string }>({
    open: false,
    url: '',
    title: '',
  })
  const [focusedLocation, setFocusedLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [inputMinimized, setInputMinimized] = useState(false)
  const isAtBottomRef = useRef(true) // Track if user is scrolled to bottom
  const hadInputRef = useRef(false) // Track if we already handled the current input
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Auto-switch to journal page and expand input only when a NEW challenge appears
  const hasInput = !!inputArea
  useEffect(() => {
    if (hasInput && !hadInputRef.current) {
      // New challenge just appeared
      if (currentPage !== 2) setCurrentPage(2)
      setInputMinimized(false)
    }
    hadInputRef.current = hasInput
  }, [hasInput])

  // Virtual paging - scroll-based pagination within each page type
  const contentRef = useRef<HTMLDivElement>(null)
  const [virtualPageCount, setVirtualPageCount] = useState(1)
  const [virtualPageIndex, setVirtualPageIndex] = useState(0)

  const {
    journal,
    isLoading,
    addNote,
    isAddingNote,
  } = useSessionJournal(sessionId)

  const { location: userLocation } = useGeolocation()

  // Page types: description, map, journal, and optionally chat (group only)
  const hasChat = !!isGroup && !!conversationId
  const totalBasePages = hasChat ? 4 : 3

  // Get current page type
  const getPageType = (page: number): PageType => {
    if (page === 0) return 'description'
    if (page === 1) return 'map'
    if (page === 2) return 'journal'
    return 'chat'
  }

  const pageType = getPageType(currentPage)

  // ========================
  // Group Chat
  // ========================
  const user = useAuthStore((s) => s.user)
  const queryClient = useQueryClient()
  const chatConversations = useChatStore((s) => s.conversations)
  const chatMessagesState = useChatStore((s) => conversationId ? s.messages[conversationId] : undefined)
  const { setMessages: setChatMessages, addMessage: addChatMessage, markConversationRead, suppressPopout, unsuppressPopout } = useChatStore()
  const {
    isConnected: chatConnected,
    joinConversation,
    leaveConversation,
    sendTypingIndicator,
    onNewMessage,
    onTypingIndicator,
    onConversationUpdateSignal,
  } = useGeneralChatConnection()
  const [typingUsers, setTypingUsers] = useState<Map<string, { userName: string; timeout: ReturnType<typeof setTimeout> }>>(new Map())

  // Suppress global popout for the adventure conversation
  useEffect(() => {
    if (!conversationId) return
    suppressPopout(conversationId)
    return () => { unsuppressPopout(conversationId) }
  }, [conversationId, suppressPopout, unsuppressPopout])

  // Fetch chat messages
  const { data: chatMessagesData } = useQuery({
    queryKey: ['chatMessages', conversationId],
    queryFn: () => chatApi.getMessages(conversationId!, 1, 50),
    enabled: !!conversationId,
  })

  useEffect(() => {
    if (chatMessagesData && conversationId) {
      const messages = Array.isArray(chatMessagesData) ? [...chatMessagesData].reverse() : []
      setChatMessages(conversationId, messages, 1, false)
    }
  }, [chatMessagesData, conversationId, setChatMessages])

  // Join SignalR chat room when on chat page
  const isChatPage = pageType === 'chat'
  useEffect(() => {
    if (!chatConnected || !conversationId || !isChatPage) return
    joinConversation(conversationId)
    chatApi.markAsRead(conversationId)
    markConversationRead(conversationId)
    return () => { leaveConversation(conversationId) }
  }, [chatConnected, conversationId, isChatPage, joinConversation, leaveConversation, markConversationRead])

  // Real-time new messages
  useEffect(() => {
    if (!chatConnected || !conversationId) return
    const unsub = onNewMessage((message) => {
      if (message.conversationId === conversationId) {
        addChatMessage(conversationId, message)
        if (isChatPage) {
          chatApi.markAsRead(conversationId)
          markConversationRead(conversationId)
        }
      }
    })
    return unsub
  }, [chatConnected, conversationId, onNewMessage, addChatMessage, isChatPage, markConversationRead])

  // ConversationUpdateSignal fallback
  useEffect(() => {
    if (!chatConnected || !conversationId) return
    const unsub = onConversationUpdateSignal?.((signal) => {
      if (signal.conversationId !== conversationId) return
      if (signal.senderId === user?.userId) return
      if (signal.updateType === 'NewMessage') {
        queryClient.invalidateQueries({ queryKey: ['chatMessages', conversationId] })
        if (isChatPage) {
          chatApi.markAsRead(conversationId)
          markConversationRead(conversationId)
        }
      } else if (signal.updateType === 'MessagesRead' || signal.updateType === 'ReactionChanged' || signal.updateType === 'MessageDeleted') {
        queryClient.invalidateQueries({ queryKey: ['chatMessages', conversationId] })
      }
    })
    return () => { unsub?.() }
  }, [chatConnected, onConversationUpdateSignal, conversationId, user?.userId, queryClient, isChatPage, markConversationRead])

  // Typing indicators
  useEffect(() => {
    if (!chatConnected || !conversationId) return
    const unsub = onTypingIndicator((indicator) => {
      if (indicator.conversationId !== conversationId || indicator.userId === user?.id) return
      setTypingUsers((prev) => {
        const newMap = new Map(prev)
        if (indicator.isTyping) {
          const existing = newMap.get(indicator.userId)
          if (existing) clearTimeout(existing.timeout)
          const timeout = setTimeout(() => {
            setTypingUsers((curr) => {
              const updated = new Map(curr)
              updated.delete(indicator.userId)
              return updated
            })
          }, 5000)
          newMap.set(indicator.userId, { userName: indicator.userName, timeout })
        } else {
          const existing = newMap.get(indicator.userId)
          if (existing) clearTimeout(existing.timeout)
          newMap.delete(indicator.userId)
        }
        return newMap
      })
    })
    return unsub
  }, [chatConnected, conversationId, onTypingIndicator, user?.id])

  // Clear typing on conversation change
  useEffect(() => {
    setTypingUsers((prev) => {
      prev.forEach(({ timeout }) => clearTimeout(timeout))
      return new Map()
    })
  }, [conversationId])

  // Send message mutation
  const sendChatMessage = useMutation({
    mutationFn: ({ content, imageId }: { content: string; imageId?: string }) =>
      chatApi.sendMessage(conversationId!, { content, imageId }),
    onSuccess: (message) => {
      addChatMessage(conversationId!, message)
    },
  })

  const handleChatSend = (content: string, imageId?: string) => {
    if (!conversationId) return
    sendChatMessage.mutate({ content, imageId })
  }

  const handleChatImageUpload = async (file: File): Promise<string | undefined> => {
    const response = await chatApi.uploadImage(file)
    return response.imageId
  }

  const handleChatTyping = (isTyping: boolean) => {
    if (!conversationId) return
    sendTypingIndicator(conversationId, isTyping)
  }

  // Unread count for chat badge
  const chatConversation = chatConversations.find((c) => c.id === conversationId)
  const chatUnreadCount = chatConversation?.unreadMessageCount ?? 0

  // Extract revealed POI locations from PoiRevealed journal entries
  const revealedPoisFromJournal = useMemo(() => {
    return journal
      .filter(entry => entry.entryType === JournalEntryType.PoiRevealed)
      .map(entry => {
        const content = entry.content as Record<string, unknown> | null
        if (!content) return null
        const lat = content.Latitude ?? content.latitude
        const lng = content.Longitude ?? content.longitude
        if (typeof lat !== 'number' || typeof lng !== 'number') return null
        return {
          id: entry.id,
          nodeId: (entry as any).workflowNodeId || entry.id,
          title: (content.Title || content.title || (entry as any).nodeLabel || '') as string,
          latitude: lat,
          longitude: lng,
          markerEmoji: (content.MarkerEmoji || content.markerEmoji || '📍') as string,
          markerStyle: (content.MarkerStyle || content.markerStyle || 'Icon') as string,
          circleRadius: (content.CircleRadius || content.circleRadius || 50) as number,
          circleColor: (content.CircleColor || content.circleColor || '#2196F3') as string,
        } as RevealedPoiFromJournal
      })
      .filter((poi): poi is RevealedPoiFromJournal => poi !== null)
  }, [journal])

  // Extract revealed routes from RouteRevealed journal entries
  const revealedRoutesFromJournal = useMemo(() => {
    return journal
      .filter(entry => entry.entryType === JournalEntryType.RouteRevealed)
      .map(entry => {
        const content = entry.content as Record<string, unknown> | null
        if (!content) return null

        // Parse route points - can be JSON string or array
        let routePoints: [number, number][] = []
        const routePointsRaw = content.RoutePointsJson || content.routePointsJson || content.RoutePoints || content.routePoints
        if (typeof routePointsRaw === 'string') {
          try {
            routePoints = JSON.parse(routePointsRaw)
          } catch {
            return null
          }
        } else if (Array.isArray(routePointsRaw)) {
          routePoints = routePointsRaw
        }

        if (!routePoints.length) return null

        return {
          id: entry.id,
          nodeId: (entry as any).workflowNodeId || entry.id,
          title: (content.Title || content.title || (entry as any).nodeLabel || 'Route') as string,
          routePoints,
          lineColor: (content.LineColor || content.lineColor || '#9C27B0') as string,
          lineStyle: (content.LineStyle || content.lineStyle || 'solid') as string,
          lineOpacity: (content.LineOpacity ?? content.lineOpacity ?? 0.8) as number,
          arrowStyle: (content.ArrowStyle || content.arrowStyle || 'none') as string,
          distanceMeters: (content.DistanceMeters || content.distanceMeters) as number | undefined,
          durationSeconds: (content.DurationSeconds || content.durationSeconds) as number | undefined,
        } as RevealedRouteFromJournal
      })
      .filter((route): route is RevealedRouteFromJournal => route !== null)
  }, [journal])

  // Filter to only show user-relevant entries
  const visibleEntries = useMemo(() => {
    return journal.filter(entry => {
      // Must be a visible entry type
      if (!VISIBLE_ENTRY_TYPES.includes(entry.entryType)) {
        return false
      }
      // For ChoiceMade entries, filter out condition evaluations (Decision nodes)
      // These have ConditionsEvaluated in content instead of a user Choice
      if (entry.entryType === JournalEntryType.ChoiceMade) {
        const content = entry.content as Record<string, unknown> | null
        if (content?.ConditionsEvaluated !== undefined) {
          return false // This is a condition evaluation, not a user choice
        }
      }
      return true
    })
  }, [journal])

  // Measure content and calculate virtual pages
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is rendered
    const measureContent = () => {
      if (!contentRef.current) return

      const container = contentRef.current
      const viewportHeight = container.clientHeight
      const contentHeight = container.scrollHeight

      // Calculate how many "pages" of content we have
      const pages = viewportHeight > 0
        ? Math.max(1, Math.ceil(contentHeight / viewportHeight))
        : 1
      setVirtualPageCount(pages)
    }

    // Measure after a short delay to allow content to render
    const timer = setTimeout(measureContent, 100)
    return () => clearTimeout(timer)
  }, [pageType, visibleEntries, adventure.description, showNoteForm])

  // Reset virtual page when changing page types
  useEffect(() => {
    setVirtualPageIndex(0)
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
    // Default to "at bottom" when switching to journal
    if (currentPage === 2) {
      isAtBottomRef.current = true
    }
  }, [currentPage])

  // Auto-scroll to bottom of journal when new entries are added (only if already at bottom)
  useEffect(() => {
    if (pageType === 'journal' && contentRef.current && visibleEntries.length > 0) {
      const container = contentRef.current
      if (isAtBottomRef.current) {
        // Use requestAnimationFrame to ensure DOM has updated with new content
        requestAnimationFrame(() => {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
        })
      }
    }
  }, [visibleEntries.length, pageType])

  const handleAddNote = () => {
    if (noteText.trim()) {
      addNote(noteText.trim())
      setNoteText('')
      setShowNoteForm(false)
    }
  }

  const handlePrevPage = () => {
    if (virtualPageIndex > 0) {
      // Scroll within current content
      const newIndex = virtualPageIndex - 1
      setVirtualPageIndex(newIndex)
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: newIndex * contentRef.current.clientHeight,
          behavior: 'smooth'
        })
      }
    } else if (currentPage > 0) {
      // Go to previous page type
      setCurrentPage(prev => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (virtualPageIndex < virtualPageCount - 1) {
      // Scroll within current content
      const newIndex = virtualPageIndex + 1
      setVirtualPageIndex(newIndex)
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: newIndex * contentRef.current.clientHeight,
          behavior: 'smooth'
        })
      }
    } else if (currentPage < totalBasePages - 1) {
      // Go to next page type
      setCurrentPage(prev => prev + 1)
    }
  }

  const handlePlayAudio = (url: string, entryId: string) => {
    if (playingAudio === entryId) {
      audioRef.current?.pause()
      setPlayingAudio(null)
    } else {
      if (audioRef.current) {
        audioRef.current.src = url
        audioRef.current.play()
        setPlayingAudio(entryId)
      }
    }
  }

  const handleAudioEnded = () => {
    setPlayingAudio(null)
  }

  const handleOpenVideo = (url: string, title: string) => {
    setVideoDialog({ open: true, url, title })
  }

  // Navigate to specific section
  const goToDescription = () => setCurrentPage(0)
  const goToMap = () => setCurrentPage(1)
  const goToJournal = () => setCurrentPage(2)
  const goToChat = () => setCurrentPage(3)

  // Switch to chat page when banner is clicked (external trigger)
  useEffect(() => {
    if (chatOpenTrigger && chatOpenTrigger > 0 && hasChat) {
      setCurrentPage(3)
    }
  }, [chatOpenTrigger])

  // Navigate to map and focus on a specific location
  const goToMapWithLocation = (lat: number, lng: number) => {
    setFocusedLocation({ lat, lng })
    setCurrentPage(1)
  }

  // Clear focused location when leaving map
  useEffect(() => {
    if (currentPage !== 1 && focusedLocation) {
      setFocusedLocation(null)
    }
  }, [currentPage])

  // Get map center
  const getMapCenter = (): [number, number] => {
    // Use focused location if navigating from journal entry
    if (focusedLocation) {
      return [focusedLocation.lat, focusedLocation.lng]
    }
    if (userLocation && userLocation.latitude && userLocation.longitude) {
      return [userLocation.latitude, userLocation.longitude]
    }
    if (locations.length > 0) {
      return [locations[0].latitude, locations[0].longitude]
    }
    // Use adventure location if available
    if (adventure.latitude && adventure.longitude) {
      return [adventure.latitude, adventure.longitude]
    }
    return [40.7128, -74.006] // Default: NYC
  }

  // Common text styling for journal feed
  const textStyle = {
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.875rem',
    lineHeight: 1.7,
    color: 'text.primary',
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      {/* Hidden audio element */}
      <audio ref={audioRef} onEnded={handleAudioEnded} />

      {/* Page content area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,  // Allow flex child to shrink below content size
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          py: 2,
          position: 'relative',
        }}
      >
        {/* Page 1: Description */}
        {pageType === 'description' && (
          <Box
            ref={contentRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              scrollbarWidth: 'thin',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                mb: 3,
                textAlign: 'center',
                color: 'text.primary',
              }}
            >
              {adventure.title}
            </Typography>

            {(adventure as any).shortDescription && (
              <Typography
                sx={{
                  ...textStyle,
                  fontStyle: 'italic',
                  mb: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                {(adventure as any).shortDescription}
              </Typography>
            )}

            <Box sx={{ my: 3, borderBottom: '1px dashed rgba(0,0,0,0.2)' }} />

            {adventure.description ? (
              <Box
                sx={{
                  ...textStyle,
                  '& p': { margin: '0.75em 0' },
                  '& strong, & b': { fontWeight: 700 },
                  '& em, & i': { fontStyle: 'italic' },
                }}
                dangerouslySetInnerHTML={{ __html: adventure.description }}
              />
            ) : (
              <Typography sx={textStyle}>
                Your adventure awaits...
              </Typography>
            )}

            {/* Adventure metadata */}
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed rgba(0,0,0,0.2)' }}>
              {(adventure as any).estimatedDuration && (
                <Typography sx={{ ...textStyle, fontSize: '0.8rem', color: 'text.secondary' }}>
                  ⏱ Estimated time: {(adventure as any).estimatedDuration} minutes
                </Typography>
              )}
              {adventure.difficulty && (
                <Typography sx={{ ...textStyle, fontSize: '0.8rem', color: 'text.secondary' }}>
                  📊 Difficulty: {adventure.difficulty}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Page 2: Map */}
        {pageType === 'map' && (
          <Box
            ref={contentRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              scrollbarWidth: 'thin',
            }}
          >
            <Box
              sx={{
                flex: 1,
                borderRadius: 2,
                overflow: 'hidden',
                minHeight: 300,
                position: 'relative',
                // Sepia filter for pencil-on-parchment look (light mode only)
                '& .leaflet-tile-pane': (theme: any) => ({
                  filter: theme.palette.mode === 'dark' ? 'none' : 'sepia(0.35) brightness(1.05) contrast(0.95)',
                }),
                // Fade edges with pseudo-element vignette
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 2,
                  boxShadow: (theme: any) => theme.palette.mode === 'dark'
                    ? 'inset 0 0 15px 8px rgba(0, 0, 0, 0.6)'
                    : 'inset 0 0 15px 8px rgba(245, 240, 230, 0.9)',
                  pointerEvents: 'none',
                  zIndex: 1000,
                },
              }}
            >
              <LazyMap
                center={getMapCenter()}
                zoom={focusedLocation ? 16 : 14}
                height="100%"
              >
                {/* Map focuser for navigating from journal entries */}
                <MapFocuser location={focusedLocation} />

                {/* User location marker */}
                {userLocation && userLocation.latitude && userLocation.longitude && userLocation.source === 'gps' && (
                  <>
                    {userLocation.accuracy && (
                      <Circle
                        center={[userLocation.latitude, userLocation.longitude]}
                        radius={userLocation.accuracy}
                        pathOptions={{
                          color: '#2196F3',
                          fillColor: '#2196F3',
                          fillOpacity: 0.1,
                          weight: 1,
                        }}
                      />
                    )}
                    <Marker
                      position={[userLocation.latitude, userLocation.longitude]}
                      icon={userLocationIcon}
                    >
                      <Popup>
                        <Typography variant="subtitle2">Your Location</Typography>
                      </Popup>
                    </Marker>
                  </>
                )}

                {/* POI markers from session store */}
                {locations.map((loc) => {
                  if (loc.markerStyle === 'none') return null
                  if (loc.markerStyle === 'circle') {
                    return (
                      <Circle
                        key={loc.nodeId}
                        center={[loc.latitude, loc.longitude]}
                        radius={loc.circleRadius || 50}
                        pathOptions={{
                          color: loc.circleColor || '#2196F3',
                          fillColor: loc.circleColor || '#2196F3',
                          fillOpacity: 0.3,
                          weight: 2,
                        }}
                      >
                        <Popup>
                          <Typography variant="subtitle2">{loc.nodeLabel}</Typography>
                          {loc.description && <Typography variant="body2">{loc.description}</Typography>}
                        </Popup>
                      </Circle>
                    )
                  }
                  return (
                    <Marker
                      key={loc.nodeId}
                      position={[loc.latitude, loc.longitude]}
                      icon={createPoiIcon(loc.markerEmoji || '📍')}
                    >
                      <Popup>
                        <Typography variant="subtitle2">{loc.nodeLabel}</Typography>
                        {loc.description && <Typography variant="body2">{loc.description}</Typography>}
                      </Popup>
                    </Marker>
                  )
                })}

                {/* POI markers from PoiRevealed journal entries */}
                {revealedPoisFromJournal.map((poi) => {
                  // Skip if already shown from locations prop
                  if (locations.some(loc => loc.nodeId === poi.nodeId)) return null
                  // Skip if marker style is 'none'
                  if (poi.markerStyle?.toLowerCase() === 'none') return null

                  if (poi.markerStyle?.toLowerCase() === 'circle') {
                    return (
                      <Circle
                        key={`journal-${poi.id}`}
                        center={[poi.latitude, poi.longitude]}
                        radius={poi.circleRadius || 50}
                        pathOptions={{
                          color: poi.circleColor || '#2196F3',
                          fillColor: poi.circleColor || '#2196F3',
                          fillOpacity: 0.3,
                          weight: 2,
                        }}
                      >
                        <Popup>
                          <Typography variant="subtitle2">{poi.title || 'Location'}</Typography>
                        </Popup>
                      </Circle>
                    )
                  }
                  return (
                    <Marker
                      key={`journal-${poi.id}`}
                      position={[poi.latitude, poi.longitude]}
                      icon={createPoiIcon(poi.markerEmoji || '📍')}
                    >
                      <Popup>
                        <Typography variant="subtitle2">{poi.title || 'Location'}</Typography>
                      </Popup>
                    </Marker>
                  )
                })}

                {/* Route polylines from RouteRevealed journal entries */}
                {revealedRoutesFromJournal.map((route) => {
                  const routeColor = route.lineColor || '#9C27B0'
                  const routeOpacity = route.lineOpacity ?? 0.8
                  const arrowStyle = route.arrowStyle?.toLowerCase()

                  return (
                    <React.Fragment key={`route-${route.id}`}>
                      {/* Route line - animated or static */}
                      {arrowStyle === 'animated' ? (
                        <AnimatedRoutePolyline
                          positions={route.routePoints}
                          color={routeColor}
                          opacity={routeOpacity}
                          weight={4}
                        />
                      ) : (
                        <Polyline
                          positions={route.routePoints}
                          pathOptions={{
                            color: routeColor,
                            weight: 4,
                            opacity: routeOpacity,
                            dashArray: getLineDashArray(route.lineStyle),
                          }}
                        >
                          <Popup>
                            <Typography variant="subtitle2">{route.title || 'Route'}</Typography>
                            {route.distanceMeters && (
                              <Typography variant="body2">
                                Distance: {(route.distanceMeters / 1000).toFixed(2)} km
                              </Typography>
                            )}
                            {route.durationSeconds && (
                              <Typography variant="body2">
                                Est. time: {Math.round(route.durationSeconds / 60)} min
                              </Typography>
                            )}
                          </Popup>
                        </Polyline>
                      )}

                      {/* Arrow at end of route */}
                      {arrowStyle === 'end' && route.routePoints.length >= 2 && (
                        <Marker
                          position={route.routePoints[route.routePoints.length - 1]}
                          icon={createRouteArrowIcon(
                            routeColor,
                            calculateBearing(
                              route.routePoints[route.routePoints.length - 2],
                              route.routePoints[route.routePoints.length - 1]
                            )
                          )}
                        />
                      )}

                      {/* Arrows along the path */}
                      {arrowStyle === 'along' && route.routePoints.length >= 2 && (
                        <>
                          {route.routePoints
                            .filter((_, i) => i > 0 && i % Math.max(1, Math.floor(route.routePoints.length / 8)) === 0)
                            .map((coord, i) => {
                              const originalIndex = route.routePoints.indexOf(coord)
                              const prevCoord = route.routePoints[originalIndex - 1]
                              return (
                                <Marker
                                  key={`${route.id}-arrow-${i}`}
                                  position={coord}
                                  icon={createRouteArrowIcon(routeColor, calculateBearing(prevCoord, coord))}
                                />
                              )
                            })}
                          {/* Always show arrow at end */}
                          <Marker
                            position={route.routePoints[route.routePoints.length - 1]}
                            icon={createRouteArrowIcon(
                              routeColor,
                              calculateBearing(
                                route.routePoints[route.routePoints.length - 2],
                                route.routePoints[route.routePoints.length - 1]
                              )
                            )}
                          />
                        </>
                      )}
                    </React.Fragment>
                  )
                })}
              </LazyMap>
            </Box>

            {/* Location and route count */}
            <Typography
              sx={{
                ...textStyle,
                fontSize: '0.8rem',
                textAlign: 'center',
                mt: 2,
                color: 'text.secondary',
              }}
            >
              {(() => {
                const totalLocations = locations.length + revealedPoisFromJournal.filter(
                  poi => !locations.some(loc => loc.nodeId === poi.nodeId)
                ).length
                const totalRoutes = revealedRoutesFromJournal.length
                const parts = []
                if (totalLocations > 0) {
                  parts.push(`📍 ${totalLocations} location${totalLocations !== 1 ? 's' : ''}`)
                }
                if (totalRoutes > 0) {
                  parts.push(`🗺️ ${totalRoutes} route${totalRoutes !== 1 ? 's' : ''}`)
                }
                return parts.length === 0
                  ? '📍 No locations discovered yet'
                  : `${parts.join(' • ')} discovered`
              })()}
            </Typography>
          </Box>
        )}

        {/* Page 3: Journal entries */}
        {pageType === 'journal' && (
          <Box
            ref={contentRef}
            onScroll={(e) => {
              const el = e.currentTarget
              // Consider "at bottom" if within 40px of the end
              isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
            }}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              scrollbarWidth: 'thin',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Journal
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowNoteForm(!showNoteForm)}
                color={showNoteForm ? 'primary' : 'default'}
              >
                <Add />
              </IconButton>
            </Box>

            {/* Add note form */}
            {showNoteForm && (
              <Box sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add a note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  disabled={isAddingNote}
                  size="small"
                  inputProps={{ maxLength: 3500 }}
                  helperText={`${noteText.length}/3500`}
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-root': {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.875rem',
                    },
                    '& .MuiFormHelperText-root': {
                      textAlign: 'right',
                      color: noteText.length > 3400 ? 'warning.main' : 'text.secondary',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => { setShowNoteForm(false); setNoteText('') }}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={isAddingNote ? <CircularProgress size={16} /> : <Send />}
                    onClick={handleAddNote}
                    disabled={!noteText.trim() || isAddingNote}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            )}

            {/* Journal content */}
            <Box sx={{ flex: 1 }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : visibleEntries.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography sx={{ ...textStyle, fontStyle: 'italic', color: 'text.secondary' }}>
                    Your journal is empty...
                  </Typography>
                  <Typography sx={{ ...textStyle, fontSize: '0.8rem', mt: 1, color: 'text.disabled' }}>
                    Notes and discoveries will appear here
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {visibleEntries.map((entry) => (
                    <JournalEntryContent
                      key={entry.id}
                      entry={entry}
                      playingAudio={playingAudio}
                      onPlayAudio={handlePlayAudio}
                      onOpenVideo={handleOpenVideo}
                      onGoToMapWithLocation={goToMapWithLocation}
                      textStyle={textStyle}
                      startedAt={startedAt}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Page 4: Group Chat */}
        {pageType === 'chat' && conversationId && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              mx: -2,  // cancel parent px: 2
              my: -2,  // cancel parent py: 2
            }}
          >
            <MessageThread
              conversationId={conversationId}
              typingUsers={Array.from(typingUsers.entries()).map(([userId, { userName }]) => ({ userId, userName }))}
            />
            <MessageInput
              onSend={handleChatSend}
              onTyping={handleChatTyping}
              onImageUpload={handleChatImageUpload}
              disabled={sendChatMessage.isPending}
              placeholder="Chat with your group..."
            />
          </Box>
        )}
      </Box>

      {/* Input area - pinned between content and bottom nav, toggled by Answer button */}
      {inputArea && !inputMinimized && (
        <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}>
          {inputArea}
        </Box>
      )}

      {/* Bottom navigation bar */}
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.75,
          flexShrink: 0,
        }}
      >
        {[
          { key: 'story', label: 'Story', icon: <MenuBook sx={{ fontSize: 18 }} />, type: 'nav' as const, navId: 0 },
          { key: 'map', label: 'Map', icon: <MapIcon sx={{ fontSize: 18 }} />, type: 'nav' as const, navId: 1 },
          { key: 'journal', label: 'Journal', icon: <NoteAlt sx={{ fontSize: 18 }} />, type: 'nav' as const, navId: 2 },
          ...(hasChat ? [{
            key: 'chat',
            label: 'Chat',
            icon: (
              <Badge badgeContent={chatUnreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, top: -2, right: -4 } }}>
                <ChatIcon sx={{ fontSize: 18 }} />
              </Badge>
            ),
            type: 'nav' as const,
            navId: 3,
          }] : []),
          { key: 'clue', label: 'Clue', icon: <Lightbulb sx={{ fontSize: 18 }} />, type: 'action' as const, active: !!hasAvailableClues },
          { key: 'answer', label: 'Answer', icon: hasPendingChallenge && challengeInputType ? getChallengeIcon(challengeInputType, 18) : <NoteAlt sx={{ fontSize: 18 }} />, type: 'action' as const, active: !!hasPendingChallenge },
        ].map((item) => {
          const isNavActive = item.type === 'nav' && currentPage === item.navId
          const isActionActive = item.type === 'action' && item.active
          const isActive = isNavActive || isActionActive
          const isDisabledAction = item.type === 'action' && !item.active

          return (
            <Box
              key={item.key}
              onClick={() => {
                if (item.type === 'nav') {
                  if (item.navId === 0) goToDescription()
                  else if (item.navId === 1) goToMap()
                  else if (item.navId === 2) goToJournal()
                  else if (item.navId === 3) goToChat()
                } else if (item.key === 'clue' && isActionActive) {
                  onRequestClue?.()
                } else if (item.key === 'answer' && isActionActive) {
                  if (currentPage === 2 && !inputMinimized) {
                    // Already showing input — toggle it off
                    setInputMinimized(true)
                  } else {
                    // Show input
                    setInputMinimized(false)
                    if (currentPage !== 2) setCurrentPage(2)
                  }
                  onChallengeClick?.()
                }
              }}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
                py: 0.75,
                cursor: isDisabledAction ? 'default' : 'pointer',
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                color: isDisabledAction
                  ? 'text.disabled'
                  : isActive
                    ? 'primary.main'
                    : 'text.disabled',
                opacity: isDisabledAction ? 0.4 : 1,
                transition: 'all 0.15s ease',
                '&:hover': isDisabledAction ? {} : {
                  color: isActive ? 'primary.main' : 'text.secondary',
                  bgcolor: isActive ? undefined : 'action.hover',
                },
                fontFamily: "'Inter', sans-serif",
                // Pulsing gradient background for active action items
                ...(isActionActive ? {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    background: (theme: any) => `radial-gradient(ellipse at center, ${theme.palette.primary.main}22 0%, transparent 70%)`,
                    animation: `${activeGlow} 1.5s ease-in-out infinite`,
                  },
                } : {}),
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex' }}>
                {item.icon}
              </Box>
              <Typography
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  fontSize: '0.6875rem',
                  fontWeight: isActive ? 600 : 500,
                  lineHeight: 1,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          )
        })}
      </Box>

      {/* Video dialog */}
      <Dialog
        open={videoDialog.open}
        onClose={() => setVideoDialog({ open: false, url: '', title: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{videoDialog.title || 'Video'}</DialogTitle>
        <DialogContent>
          {videoDialog.url && (
            <video
              src={videoDialog.url}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

// Format date/time for journal entries: "12/18/2025 05:45:09 AM"
// Converts UTC time from API to user's local time
function formatEntryDate(dateStr: string): string {
  // API returns dates without 'Z' suffix but they are UTC, so append 'Z' if missing
  const utcDateStr = dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`
  const date = new Date(utcDateStr)
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

// Format duration from session start as hh:mm:ss
function formatDuration(entryDateStr: string, startedAt: string | null): string {
  if (!startedAt) return '00:00:00'
  const utcEntry = entryDateStr.endsWith('Z') ? entryDateStr : `${entryDateStr}Z`
  const utcStart = startedAt.endsWith('Z') ? startedAt : `${startedAt}Z`
  const diffMs = new Date(utcEntry).getTime() - new Date(utcStart).getTime()
  const totalSec = Math.max(0, Math.floor(diffMs / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// Format adventure clock seconds as HH:MM:SS
function formatAdventureClockSeconds(seconds: number): string {
  const totalSec = Math.max(0, Math.floor(seconds))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// Clickable clock icon that shows duration, with popover for full date/time
function JournalTimestamp({ entryDate, startedAt, adventureClockSeconds, textStyle }: { entryDate: string; startedAt: string | null; adventureClockSeconds?: number; textStyle: Record<string, unknown> }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const duration = formatDuration(entryDate, startedAt)
  const fullDate = formatEntryDate(entryDate)
  const displayTime = adventureClockSeconds != null
    ? formatAdventureClockSeconds(adventureClockSeconds)
    : duration

  return (
    <>
      <Box
        component="span"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.3,
          cursor: 'pointer',
          color: 'text.disabled',
          borderRadius: 0.5,
          '&:hover': { color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.04)' },
          px: 0.3,
          mr: 0.5,
        }}
      >
        <AccessTime sx={{ fontSize: 13 }} />
        <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem' }}>
          {displayTime}
        </Typography>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 220 } } }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {fullDate}
        </Typography>
        {adventureClockSeconds != null && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
            Adventure time: {formatAdventureClockSeconds(adventureClockSeconds)}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
          Elapsed: {duration}
        </Typography>
      </Popover>
    </>
  )
}

// Individual journal entry content renderer
interface JournalEntryContentProps {
  entry: JournalEntryDto
  playingAudio: string | null
  onPlayAudio: (url: string, entryId: string) => void
  onOpenVideo: (url: string, title: string) => void
  onGoToMapWithLocation?: (lat: number, lng: number) => void
  textStyle: Record<string, unknown>
  startedAt: string | null
}

function JournalEntryContent({ entry, playingAudio, onPlayAudio, onOpenVideo, onGoToMapWithLocation, textStyle, startedAt }: JournalEntryContentProps) {
  const isPlaying = playingAudio === entry.id
  // Support both camelCase and PascalCase from API (defensive)
  const clockSeconds = entry.adventureClockSeconds ?? (entry as any).AdventureClockSeconds
  const timestampEl = <JournalTimestamp entryDate={entry.createdAt} startedAt={startedAt} adventureClockSeconds={clockSeconds} textStyle={textStyle} />

  switch (entry.entryType) {
    case JournalEntryType.FormattedMessage:
      // Content is in entry.content.HtmlContent from API
      const htmlContent = entry.htmlContent || (entry.content as any)?.HtmlContent || (entry.content as any)?.htmlContent
      return (
        <Box
          sx={{
            p: 2,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.08)' : 'rgba(212,168,83,0.06)'}, ${theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.03)' : 'rgba(212,168,83,0.02)'})`,
            borderLeft: 3,
            borderColor: 'warning.main',
            borderRadius: '0 10px 10px 0',
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1,
          }}>
            <Typography sx={{
              fontSize: '0.625rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: 'warning.main',
            }}>
              ◆ Narrator
            </Typography>
            {timestampEl}
          </Box>
          {htmlContent ? (
            <Box
              sx={{
                fontFamily: "'Lora', serif",
                fontSize: '0.875rem',
                lineHeight: 1.7,
                color: 'text.primary',
                '& p': { margin: '0.5em 0' },
                '& strong, & b': { fontWeight: 700 },
                '& em, & i': { fontStyle: 'italic' },
                '& pre': {
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  padding: '8px',
                  borderRadius: '4px',
                  margin: '0.5em 0',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& code': {
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                  borderRadius: 0,
                },
                '& blockquote': {
                  borderLeft: 4,
                  borderColor: 'primary.main',
                  margin: '0.5em 0',
                  paddingLeft: '12px',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                },
                '& blockquote p': {
                  margin: '0.25em 0',
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  mt: 1.5,
                },
              }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            <Typography sx={{ fontFamily: "'Lora', serif", fontSize: '0.875rem', lineHeight: 1.7, color: 'text.primary' }}>
              {entry.textContent || (entry.content as any)?.Title || 'Message received'}
            </Typography>
          )}
        </Box>
      )

    case JournalEntryType.TextClue: {
      const clueContent = entry.content as Record<string, unknown> | null
      const cluesAvailable = clueContent?.CluesAvailable ?? clueContent?.cluesAvailable ?? clueContent?.AvailableCount ?? clueContent?.availableCount
      const clueCount = clueContent?.ClueCount ?? clueContent?.clueCount ?? clueContent?.Count ?? clueContent?.count
      const isUnlock = clueContent?.IsUnlock ?? clueContent?.isUnlock
      const cluesRemoved = cluesAvailable === 0 || clueCount === 0 || isUnlock === false || clueContent?.CluesRemoved === true || clueContent?.cluesRemoved === true
      const clueMessage = cluesRemoved ? 'Clues no longer available' : 'New clues unlocked'
      const clueEmoji = cluesRemoved ? '🔒' : '🔓'

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 0.75,
            fontSize: '0.6875rem',
            color: 'text.disabled',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: 500,
          }}
        >
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          <span>{clueEmoji}</span>
          <span>{clueMessage}</span>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        </Box>
      )
    }

    case JournalEntryType.ClueRequested: {
      const crHtml = entry.clueHtmlContent || (entry.content as any)?.content || (entry.content as any)?.Content || (entry.content as any)?.htmlContent || (entry.content as any)?.HtmlContent
      return (
        <Box
          sx={{
            background: (theme) => theme.palette.mode === 'dark' ? 'rgba(66, 153, 225, 0.06)' : 'rgba(66, 153, 225, 0.08)',
            border: 1,
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(66, 153, 225, 0.15)' : 'rgba(66, 153, 225, 0.2)',
            borderRadius: 2.5,
            p: 1.5,
            display: 'flex',
            gap: 1.25,
          }}
        >
          <Box sx={{
            width: 32,
            height: 32,
            background: 'rgba(66, 153, 225, 0.15)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '14px',
          }}>
            💡
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'info.main', mb: 0.5 }}>
              Hint Used
            </Typography>
            {timestampEl}
            {crHtml ? (
              <Box
                dangerouslySetInnerHTML={{ __html: crHtml }}
                sx={{ fontSize: '0.8rem', lineHeight: 1.6, color: 'text.secondary', fontStyle: 'italic', '& p': { mt: 0, mb: 0.5 } }}
              />
            ) : (
              <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.6, color: 'text.secondary', fontStyle: 'italic' }}>
                {entry.textContent || 'A clue was requested'}
              </Typography>
            )}
          </Box>
        </Box>
      )
    }

    case JournalEntryType.UserNote:
      // Content is in entry.content.note from API
      const noteText = entry.note || (entry.content as any)?.note || entry.textContent || 'Personal note'
      return (
        <Box sx={{
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2.5,
          p: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}>
          <Box sx={{ flexShrink: 0, fontSize: '14px' }}>📝</Box>
          <Box sx={{ flex: 1 }}>
            {timestampEl}
            <Typography sx={{ ...textStyle, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>
              {noteText}
            </Typography>
          </Box>
        </Box>
      )

    case JournalEntryType.CodexInputEntered:
    case JournalEntryType.NumericInputEntered:
    case JournalEntryType.ChoiceMade: {
      // Parse content - can be object or string
      const cmContent = typeof entry.content === 'string'
        ? JSON.parse(entry.content)
        : entry.content || {}
      // Result contains the text, Choice contains the GUID
      const cmUserChoice = cmContent.Result || cmContent.result || cmContent.Choice || cmContent.choice || 'Unknown'
      const cmBranchKey = cmContent.BranchKey || cmContent.branchKey || ''
      const cmIsCorrect = cmContent.IsCorrect ?? cmContent.isCorrect ?? (cmBranchKey === 'correct')
      const cmWasSkipped = cmBranchKey === 'skip'
      const cmWasCancelled = cmBranchKey === 'cancel'
      const cmWasTimeout = cmBranchKey === 'timeout'

      // Determine display style based on result
      const cmIsNeutral = cmWasSkipped || cmWasCancelled || cmWasTimeout
      const cmBgColor = cmIsNeutral
        ? 'rgba(158, 158, 158, 0.15)'
        : cmIsCorrect
          ? 'rgba(76, 175, 80, 0.15)'
          : 'rgba(244, 67, 54, 0.15)'
      const cmBorderColor = cmIsNeutral
        ? 'grey.400'
        : cmIsCorrect
          ? 'success.light'
          : 'error.light'
      const cmResultColor = cmIsNeutral
        ? 'text.secondary'
        : cmIsCorrect
          ? 'success.main'
          : 'error.main'

      // Determine result text - combined with answer on one line
      let cmResultText = ''
      if (cmWasSkipped) {
        cmResultText = '⏭ Skipped'
      } else if (cmWasCancelled) {
        cmResultText = '✗ Cancelled'
      } else if (cmWasTimeout) {
        cmResultText = '⏱ Timed out'
      } else if (cmIsCorrect) {
        cmResultText = `✓ Correct Answer: ${cmUserChoice}`
      } else {
        cmResultText = `✗ Incorrect Answer: ${cmUserChoice}`
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1.25,
            borderRadius: 2.5,
            bgcolor: cmBgColor,
            border: 1,
            borderColor: cmBorderColor,
          }}
        >
          <Box sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            flexShrink: 0,
            bgcolor: cmIsNeutral ? 'rgba(158,158,158,0.2)' : cmIsCorrect ? 'rgba(72,187,120,0.2)' : 'rgba(252,129,129,0.2)',
          }}>
            {cmIsNeutral ? (cmWasSkipped ? '⏭' : cmWasCancelled ? '✗' : '⏱') : cmIsCorrect ? '✓' : '✗'}
          </Box>
          <Box sx={{ flex: 1 }}>
            {timestampEl}
            <Typography
              component="span"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: cmResultColor,
              }}
            >
              {cmResultText}
            </Typography>
          </Box>
        </Box>
      )
    }

    // PoiLocation and PoiRevealed both have value 6 - handle together
    case JournalEntryType.PoiRevealed: {
      const poiContent = entry.content as Record<string, unknown> | null
      const poiTitle = (poiContent?.Title || poiContent?.title || (entry as any).markerNote || (entry as any).nodeLabel) as string | null
      const poiLat = (poiContent?.Latitude ?? poiContent?.latitude) as number | undefined
      const poiLng = (poiContent?.Longitude ?? poiContent?.longitude) as number | undefined
      const poiEmoji = (poiContent?.MarkerEmoji || poiContent?.markerEmoji || entry.markerEmoji || '📍') as string
      const hasLocation = typeof poiLat === 'number' && typeof poiLng === 'number'
      const displayText = poiTitle || 'A new location was added to your map'

      const handleClick = () => {
        if (hasLocation && onGoToMapWithLocation) {
          onGoToMapWithLocation(poiLat!, poiLng!)
        }
      }

      return (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'rgba(33, 150, 243, 0.1)',
            borderRadius: 1,
            cursor: hasLocation && onGoToMapWithLocation ? 'pointer' : 'default',
            '&:hover': hasLocation && onGoToMapWithLocation ? {
              bgcolor: 'rgba(33, 150, 243, 0.2)',
            } : {},
          }}
          onClick={handleClick}
        >
          {timestampEl}
          <Typography
            component="span"
            sx={{
              ...textStyle,
              color: hasLocation && onGoToMapWithLocation ? 'primary.main' : 'inherit',
              textDecoration: hasLocation && onGoToMapWithLocation ? 'underline' : 'none',
              '&:hover': hasLocation && onGoToMapWithLocation ? {
                color: 'primary.dark',
              } : {},
            }}
          >
            {poiEmoji} {displayText}
          </Typography>
        </Box>
      )
    }

    case JournalEntryType.RouteRevealed: {
      const routeContent = entry.content as Record<string, unknown> | null
      const routeTitle = (routeContent?.Title || routeContent?.title || (entry as any).nodeLabel || 'Route') as string
      const routeDistance = (routeContent?.DistanceMeters || routeContent?.distanceMeters) as number | undefined
      const routeDuration = (routeContent?.DurationSeconds || routeContent?.durationSeconds) as number | undefined
      const routeTravelMode = (routeContent?.TravelMode || routeContent?.travelMode) as string | undefined

      // Format travel mode for display
      const travelModeDisplay = routeTravelMode?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

      return (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'rgba(156, 39, 176, 0.1)',
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(156, 39, 176, 0.2)',
            },
          }}
          onClick={() => {
            // Navigate to map page when clicking route
            if (onGoToMapWithLocation) {
              // Try to get first coordinate from route points
              const routePointsRaw = routeContent?.RoutePointsJson || routeContent?.routePointsJson || routeContent?.RoutePoints || routeContent?.routePoints
              let firstPoint: [number, number] | null = null
              if (typeof routePointsRaw === 'string') {
                try {
                  const points = JSON.parse(routePointsRaw)
                  if (points.length > 0) firstPoint = points[0]
                } catch { /* ignore */ }
              } else if (Array.isArray(routePointsRaw) && routePointsRaw.length > 0) {
                firstPoint = routePointsRaw[0] as [number, number]
              }
              if (firstPoint) {
                onGoToMapWithLocation(firstPoint[0], firstPoint[1])
              }
            }
          }}
        >
          {timestampEl}
          <Typography
            component="span"
            sx={{
              ...textStyle,
              color: 'secondary.main',
              textDecoration: 'underline',
            }}
          >
            🗺️ {routeTitle}
          </Typography>
          {(routeDistance || routeDuration || travelModeDisplay) && (
            <Typography
              sx={{
                ...textStyle,
                fontSize: '0.95rem',
                color: 'text.secondary',
                display: 'block',
                mt: 0.5,
              }}
            >
              {travelModeDisplay && `${travelModeDisplay}`}
              {routeDistance && ` • ${(routeDistance / 1000).toFixed(2)} km`}
              {routeDuration && ` • ${Math.round(routeDuration / 60)} min`}
            </Typography>
          )}
        </Box>
      )
    }

    case JournalEntryType.Image:
      const imageUrl = entry.content?.url || (entry.content as any)?.imageUrl || (entry.content as any)?.ImageUrl
      return (
        <Box>
          {timestampEl}
          <Typography component="span" sx={textStyle}>An image was displayed</Typography>
          {imageUrl && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Box
                component="img"
                src={imageUrl}
                alt="Journal image"
                sx={{ maxWidth: '66%', height: 'auto', borderRadius: 1, boxShadow: 2 }}
              />
            </Box>
          )}
          {(entry.content as any)?.caption && (
            <Typography sx={{ ...textStyle, fontSize: '1rem', fontStyle: 'italic', mt: 1 }}>
              {(entry.content as any).caption}
            </Typography>
          )}
        </Box>
      )

    case JournalEntryType.AudioPlayed:
      const audioUrl = entry.content?.url || (entry.content as any)?.audioUrl || (entry.content as any)?.AudioUrl
      return (
        <Box sx={{ p: 1.5, bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 1 }}>
          {timestampEl}
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => audioUrl && onPlayAudio(audioUrl, entry.id)}
              disabled={!audioUrl}
              color="primary"
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Typography component="span" sx={textStyle}>
              {(entry.content as any)?.title || (entry.content as any)?.Title || 'Audio recording'}
            </Typography>
          </Box>
        </Box>
      )

    case JournalEntryType.VideoPlayed:
      const videoUrl = entry.content?.url || (entry.content as any)?.videoUrl || (entry.content as any)?.VideoUrl
      const videoTitle = (entry.content as any)?.title || (entry.content as any)?.Title || 'Video'
      return (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'rgba(233, 30, 99, 0.1)',
            borderRadius: 1,
            cursor: videoUrl ? 'pointer' : 'default',
          }}
          onClick={() => videoUrl && onOpenVideo(videoUrl, videoTitle)}
        >
          {timestampEl}
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <PlayCircleOutline color="primary" fontSize="small" />
            <Typography component="span" sx={textStyle}>
              {videoTitle} — tap to play
            </Typography>
          </Box>
        </Box>
      )

    case JournalEntryType.SessionCompleted:
      return (
        <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.15)', borderRadius: 1 }}>
          {timestampEl}
          <Typography component="span" sx={{ ...textStyle, fontSize: '1.4rem', color: 'success.main' }}>
            🎉 Adventure Completed!
          </Typography>
        </Box>
      )

    case JournalEntryType.SessionFailed:
      return (
        <Box sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.15)', borderRadius: 1 }}>
          {timestampEl}
          <Typography component="span" sx={{ ...textStyle, fontSize: '1.4rem', color: 'error.main' }}>
            Adventure Failed
          </Typography>
        </Box>
      )

    case JournalEntryType.ModeratorApprovalReceived: {
      const approvalContent = entry.content as Record<string, unknown> | null
      const wasApproved = approvalContent?.Approved ?? approvalContent?.approved
      const denialReason = (approvalContent?.RejectionReason ?? approvalContent?.rejectionReason ?? approvalContent?.Reason ?? approvalContent?.reason) as string | undefined
      const approvalMessage = wasApproved ? 'Moderator approved' : 'Moderator denied'
      const approvalEmoji = wasApproved ? '✅' : '❌'

      return (
        <Box sx={{ py: 0.75 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              fontSize: '0.6875rem',
              color: 'text.disabled',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: 500,
            }}
          >
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <span>{approvalEmoji}</span>
            <span>{approvalMessage}</span>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>
          {!wasApproved && denialReason && (
            <Typography sx={{
              ...textStyle, fontSize: '0.75rem', color: 'text.secondary',
              textAlign: 'center', mt: 0.5, fontStyle: 'italic',
              textTransform: 'none',
            }}>
              Reason: {denialReason}
            </Typography>
          )}
        </Box>
      )
    }

    case JournalEntryType.ChallengePresented:
      const challengeType = (entry.content as any)?.inputType || (entry.content as any)?.InputType || 'QuestionAnswer'
      const challengeLabel = challengeType === 'QuestionAnswer' ? '🧩 Question'
        : challengeType === 'Numpad' ? '🔒 Code Required'
        : challengeType === 'QRCode' ? '📷 QR Code Scan'
        : challengeType === 'ModeratorApproval' ? '🛡️ Moderator Check' : '🧩 Challenge'
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            border: 1,
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212, 168, 83, 0.3)' : 'rgba(212, 168, 83, 0.2)',
          }}
        >
          <Box sx={{
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))'
              : 'linear-gradient(135deg, rgba(0,0,0,0.04), rgba(0,0,0,0.02))',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}>
            <Box sx={{
              width: 36,
              height: 36,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              flexShrink: 0,
            }}>
              🔍
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'warning.main' }}>
                {challengeLabel}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            {timestampEl}
            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.7, color: 'text.secondary' }}>
              {(entry.content as any)?.questionText || (entry.content as any)?.QuestionText || entry.textContent || 'Challenge presented'}
            </Typography>
          </Box>
        </Box>
      )

    case JournalEntryType.ChallengeAnswered:
      const isCorrect = (entry.content as any)?.isCorrect ?? (entry.content as any)?.IsCorrect
      const answerValue = (entry.content as any)?.answer || (entry.content as any)?.Answer || entry.choice
      return (
        <Box
          sx={{
            p: 1.5,
            bgcolor: isCorrect ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
            borderRadius: 1,
            border: 2,
            borderColor: isCorrect ? 'success.light' : 'error.light',
          }}
        >
          <Box>
            {timestampEl}
            <Typography component="span" sx={textStyle}>
              Answer: <strong>{answerValue || 'Submitted'}</strong>
            </Typography>
          </Box>
          <Typography
            sx={{
              ...textStyle,
              fontSize: '1rem',
              color: isCorrect ? 'success.main' : 'error.main',
            }}
          >
            {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
          </Typography>
        </Box>
      )

    case JournalEntryType.TimerPaused: {
      const pauseReason = (entry.content as any)?.Reason || (entry.content as any)?.reason || 'Timer Paused'
      return (
        <Box>
          {timestampEl}
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <PauseCircle sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography component="span" sx={{ ...textStyle, color: 'warning.main' }}>
              {pauseReason}
            </Typography>
          </Box>
        </Box>
      )
    }

    case JournalEntryType.TimerResumed: {
      const resumeReason = (entry.content as any)?.Reason || (entry.content as any)?.reason || 'Timer Resumed'
      return (
        <Box>
          {timestampEl}
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <PlayCircleOutline sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography component="span" sx={{ ...textStyle, color: 'success.main' }}>
              {resumeReason}
            </Typography>
          </Box>
        </Box>
      )
    }

    default:
      return null
  }
}
